import React, { useState, useEffect } from 'react';
import { Package, Loader2 } from 'lucide-react';

interface NFT {
  id: string;
  name: string;
  description: string;
  image_url: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  nft_type: string;
  mint_number?: number;
  total_supply?: number;
  tradeable: boolean;
  acquired_at: string;
  created_at: string;
}

interface NFTCollectionProps {
  token: string;
}

const NFTCollection: React.FC<NFTCollectionProps> = ({ token }) => {
  const [nfts, setNfts] = useState<NFT[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchNFTs();
  }, [token]);

  const fetchNFTs = async () => {
    try {
      const response = await fetch('https://ahantravyuh.com/api/nfts', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch NFTs');
      }

      const data = await response.json();
      setNfts(data.data?.nfts || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'legendary':
        return 'from-yellow-500 to-orange-500';
      case 'epic':
        return 'from-purple-500 to-pink-500';
      case 'rare':
        return 'from-blue-500 to-cyan-500';
      case 'uncommon':
        return 'from-green-500 to-emerald-500';
      default:
        return 'from-gray-500 to-slate-500';
    }
  };

  const getRarityBorder = (rarity: string) => {
    switch (rarity) {
      case 'legendary':
        return 'border-yellow-400';
      case 'epic':
        return 'border-purple-400';
      case 'rare':
        return 'border-blue-400';
      case 'uncommon':
        return 'border-green-400';
      default:
        return 'border-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
          <p className="text-white text-lg">Loading your collection...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <Package className="w-8 h-8 text-white" />
          <h2 className="text-3xl font-bold text-white">My NFT Collection</h2>
        </div>
        <p className="text-gray-300">
          You own {nfts.length} NFT{nfts.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* NFT Grid */}
      {nfts.length === 0 ? (
        <div className="text-center py-16">
          <Package className="w-24 h-24 text-gray-500 mx-auto mb-4 opacity-50" />
          <p className="text-gray-400 text-xl">No NFTs yet</p>
          <p className="text-gray-500 mt-2">Start playing to earn your first NFT!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {nfts.map((nft) => (
            <div
              key={nft.id}
              className={`bg-white bg-opacity-10 backdrop-blur-lg rounded-xl overflow-hidden border-2 ${getRarityBorder(
                nft.rarity
              )} hover:scale-105 transition-transform duration-300 cursor-pointer`}
            >
              {/* NFT Image */}
              <div className="aspect-square bg-gradient-to-br from-gray-800 to-gray-900 relative overflow-hidden">
                {nft.image_url ? (
                  <img
                    src={nft.image_url}
                    alt={nft.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-20 h-20 text-gray-600" />
                  </div>
                )}
                {/* Rarity Badge */}
                <div
                  className={`absolute top-2 right-2 px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${getRarityColor(
                    nft.rarity
                  )}`}
                >
                  {nft.rarity.toUpperCase()}
                </div>
              </div>

              {/* NFT Info */}
              <div className="p-4">
                <h3 className="text-white font-bold text-lg mb-2 truncate">{nft.name}</h3>
                <p className="text-gray-300 text-sm mb-3 line-clamp-2">{nft.description}</p>
                <p className="text-gray-400 text-xs">
                  Acquired: {new Date(nft.acquired_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NFTCollection;
